import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue'
import { useIntervalFn } from '@vueuse/core'
import { globalUtils } from '@gat-ship/utils'
import type { SupportSession } from '@gat-ship/types'
import {
  type CountdownParts,
  type CountdownWarningLevel,
  formatTimeRemaining,
  isCountdownExpired,
  supportSessionWarningLevel
} from '../model/support-session-utils'

export type { CountdownWarningLevel }

/**
 * Ticking countdown to a support session's `grantedTo`. Updates every
 * second and stops once the session has expired. Drives the status cell
 * badge and the in-session header.
 */
export function useSupportSessionCountdown(
  grantedTo: MaybeRefOrGetter<SupportSession['grantedTo'] | undefined>
) {
  const timeRemaining = ref<CountdownParts>()

  /** Expired sessions have run past `grantedTo` (all parts at or below zero). */
  const isExpired = computed(() => isCountdownExpired(timeRemaining.value))

  /** Largest-unit label of the remaining time, or 'Ended'. */
  const label = computed(() =>
    timeRemaining.value ? formatTimeRemaining(timeRemaining.value) : ''
  )

  /** 'critical' inside 5 minutes (or expired), 'warning' inside 20. */
  const warningLevel = computed<CountdownWarningLevel>(() =>
    supportSessionWarningLevel(timeRemaining.value, isExpired.value)
  )

  const { pause, resume } = useIntervalFn(tick, 1000, { immediate: false })

  function tick() {
    const value = toValue(grantedTo)
    if (!value) return
    timeRemaining.value = globalUtils.date.timeRemaining(value)
    if (isExpired.value) {
      pause()
    }
  }

  watch(
    () => toValue(grantedTo),
    (value) => {
      if (value) {
        tick()
        resume()
      } else {
        pause()
      }
    },
    { immediate: true }
  )

  return {
    isExpired,
    label,
    warningLevel
  }
}
